import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';

import {
  AppProvider, Page, Card, DescriptionList, Banner, Layout, Badge, Frame,
  Toast, Spinner, Thumbnail, Link, Form, TextField, PageActions, Button, Select
} from '@shopify/polaris';

import { ApolloProvider, gql, useQuery, useMutation } from '@apollo/client';
import { adminClient, userErrorsToFormik } from 'api/index';

import PageSkeleton from './PageSkeleton';
import snakeCase from 'lodash/snakeCase';
import I18n from 'i18n-js';

import PolarisBooleanBadge from '@components/shared/ui/PolarisBooleanBadge';
import GeneralModal from 'components/shared/GeneralModal';

import CustomerDataSection from './components/CustomerDataSection';
import CustomerTicketComments from './components/CustomerTicketComments';
import CustomerTicketAdminMedia from './components/CustomerTicketAdminMedia';
import CustomerLaneSection from './components/CustomerLaneSection';
import SupplierCaseSection from './components/SupplierCaseSection';

import RelatedRecordsSection from './components/RelatedRecordsSection';

import { polarisBuildListItems } from 'utils/index';

const propTypes = {
  recordId: PropTypes.number.isRequired,
}

const defaultProps = {

}

const CustomerTicketDetails = (props) => {
  const [toast, setToast] = useState(null);

  // =================== API Hooks ==========================
  const { data, loading, error, refetch } = useQuery(RECORD_GET, {
    fetchPolicy: 'network-only',
    variables: {
      id: props.recordId
    }
  });

  // =======================================================

  // >>> First loading <<<
  // Load skeleton if required data hasn't loaded at least once
  if(!data){
    return <PageSkeleton />
  }

  const customerTicket = data.customerTicket;
  const actionsLoading = loading; // Loader on top of content

  const adminDataItems = polarisBuildListItems([
    // requireItems - Only display if true or when ticket has been approved/rejected meaning requiral has been explicitly set.
    // Not shown from the start to avoid confusion as "No" required can be interpreted as not required but also as not set.
    { name: "approvalKind", hideIfEmpty: true, accessor: (value) => I18n.t(`enum.approval_kind.${snakeCase(value)}`) },
    {
      name: "requireItems", 
      accessor: (value) => <PolarisBooleanBadge value={value} />, 
      // Hidden while the approval kind is shown: it already says whether the product is required
      hideIf: (value, record) => !record.requireItemsDisplayable || !!record.approvalKind
    },
    { name: "itemsReceived", accessor: (value) => <PolarisBooleanBadge value={value} /> },
    { name: "brightpearlSalesCreditId", hideIfEmpty: true, accessor: (value, record) => 
      <Link external url={record.brightpearlSalesCreditUrl}>{value}</Link>
    },
    { name: "createdAt", accessor: (value) => I18n.l("time.formats.app_datetime", value) },
  ], customerTicket);

  // Just links to other CustomerTickets related to the same order
  const relatedOrderCustomerTickets = customerTicket.relatedOrderCustomerTickets.map(ticket => {
    return {
      term: ticket.name,
      description: (
        <Link external url={`/customer_tickets/${ticket.displayId}`}>
          { ticket.name } - { I18n.t(`enum.ticket_kind.${snakeCase(ticket.ticketKind)}`) }
        </Link>
      )
    }
  });

  return (
    <div className="polaris-page-customer-ticket-details">
      <Page
        fullWidth
        title={
          <>
            <span>{ customerTicket.name }</span>
            { customerTicket.keyCustomerName &&
              <span className='ml8'><Badge status="info">{ customerTicket.keyCustomerName }</Badge></span>
            }
            <span className='ml8'>{ actionsLoading && <Spinner color='teal' size="small" /> }</span>
          </>
        }
        breadcrumbs={[{ content: I18n.t("app.customer_tickets.title"), url: "/customer_tickets" }]}
      >
        <Layout>
          { !!toast && <Toast content={toast} onDismiss={() => setToast(null)} /> }

          {/* Left section - what to do next: the two lanes first, then the conversation around them */}
          <Layout.Section>
            <>
              {customerTicket.awaitingPickupLocation &&
                <div className="mt12">
                  <PickupLocationPendingBanner customerTicketId={customerTicket.id} onToast={setToast}>
                    {/* The customer may have brought it anyway: receiving it here ends the wait for the location */}
                    {customerTicket.receivableWithoutTask &&
                      <ReceiveWithoutTaskAction customerTicketId={customerTicket.id} onToast={setToast} onRefetch={refetch} asButton />
                    }
                  </PickupLocationPendingBanner>
                </div>
              }

              {/* One predicate, shared with the model, so the page and the backend cannot drift into
                  offering an action the backend refuses. */}
              {customerTicket.receivableAtStore &&
                <div className="mt12">
                  <MarkReceivedAtStoreAction customerTicketId={customerTicket.id} onToast={setToast} onRefetch={refetch} />
                </div>
              }

              {/* No task left to bring the product (never created, or archived): staff receives it directly.
                  A store drop-off not yet handed over goes through the store first, so only one banner shows. */}
              {customerTicket.receivableWithoutTask && !customerTicket.receivableAtStore && !customerTicket.awaitingPickupLocation &&
                <div className="mt12">
                  <ReceiveWithoutTaskAction customerTicketId={customerTicket.id} onToast={setToast} onRefetch={refetch} />
                </div>
              }

            </>

            {/* The two lanes of the case, same anatomy, resolved independently */}
            <CustomerLaneSection
              customerTicket={customerTicket}
              onToast={setToast}
              onRefetch={refetch}
              footer={!customerTicket.readOnly && (
                <TicketCancelButton customerTicket={customerTicket} onToast={setToast} onRefetch={refetch} />
              )}
            />

            <SupplierCaseSection
              customerTicket={customerTicket}
              onToast={setToast}
              onRefetch={refetch}
            />

            <CustomerTicketComments
              customerTicketId={customerTicket.id}
              onToast={setToast}
              as={ ({ children }) =>
                <Card>
                  <Card.Section title="Comentarios">
                    { children }
                  </Card.Section>
                </Card>
              }
            />

          </Layout.Section>

          {/* Right section - who the case is about, then admin and related records */}
          <Layout.Section secondary>
            <Card>
              <CustomerDataSection customerTicket={customerTicket} />
            </Card>

            <Card>
              <Card.Section title="Admin">
                <DescriptionList
                  items={adminDataItems}
                />
              </Card.Section>
            </Card>

            { relatedOrderCustomerTickets.length > 0 &&
              <Card>
                <Card.Section title="RMA relacionados a la Orden">
                  <DescriptionList
                    items={relatedOrderCustomerTickets}
                  />
                </Card.Section>
              </Card>
            }

            <Card>
              <RelatedRecordsSection customerTicket={customerTicket} />
            </Card>

            <CustomerTicketAdminMedia
              customerTicketId={customerTicket.id}
              onToast={setToast}
              as={ ({ children, primaryFooterAction }) =>
                <Card primaryFooterAction={primaryFooterAction}>
                  <Card.Section title="Admin media">
                    { children }
                  </Card.Section>
                </Card>
              }
            />
          </Layout.Section>
        </Layout>
      </Page>
    </div>
  )
};

const PickupLocationPendingBanner = ({ customerTicketId, onToast, children }) => {
  const [regenerateLink, { loading }] = useMutation(REGENERATE_PICKUP_LOCATION_LINK, {
    onCompleted: (data) => {
      const { payload, errors } = data.customerTicketRegeneratePickupLocationLink;
      if (payload) {
        navigator.clipboard.writeText(payload)
          .then(() => onToast("Link copiado al portapapeles"))
          .catch(() => onToast("No pudimos copiar el link al portapapeles"));
      } else {
        onToast(errors[0]?.message || I18n.t("messages.one_error"));
      }
    },
    onError: () => onToast(I18n.t("messages.request_error")),
  });

  // Banner's action button in this Polaris version has no loading/disabled visual, so guard
  // against a double-click firing the mutation twice while the first call is still in flight.
  const handleCopyLink = () => {
    if (loading) return;
    regenerateLink({ variables: { id: customerTicketId } });
  };

  return (
    <Banner
      title="Esperando ubicación del cliente"
      status="warning"
      action={{ content: 'Generar y copiar nuevo link', onAction: handleCopyLink }}
    >
      <p>El cliente todavía no ha confirmado dónde retirar el producto.</p>
      {children && <div className="mt12">{children}</div>}
    </Banner>
  );
};

// Row 1 of the RMA product-tracking automation table - creates the store -> CEDI Task that
// propagate_rma_leg_transition!/register_reception_at_cedi! then advance automatically.
// Store choices come from the real PickupPoint catalog (Unimart's actual retail locations) rather
// than free text - mark_received_at_store! still only takes a plain address string, so we send the
// selected store's name as that string; PickupPoint has no street-address field of its own.
const MarkReceivedAtStoreAction = ({ customerTicketId, onToast, onRefetch }) => {
  const modalRef = useRef(null);
  const [pickupPointName, setPickupPointName] = useState('');
  const [formError, setFormError] = useState(null);

  // The selector lists every store, so one capped page would silently hide the ones past it and the
  // operator could not pick them. Page 1 arrives first and the rest are pulled in behind it.
  const { data: pickupPointsData, fetchMore: fetchMorePickupPoints } = useQuery(
    MARK_RECEIVED_PICKUP_POINTS_QUERY, { client: adminClient, variables: { page: 1 } },
  );
  const loadedPage = pickupPointsData?.pickupPoints?.metadata?.page ?? 0;
  const totalPages = pickupPointsData?.pickupPoints?.metadata?.pages ?? 0;

  useEffect(() => {
    if (loadedPage === 0 || loadedPage >= totalPages) return;

    fetchMorePickupPoints({
      variables: { page: loadedPage + 1 },
      updateQuery: (previous, { fetchMoreResult }) => (
        fetchMoreResult
          ? {
            pickupPoints: {
              ...fetchMoreResult.pickupPoints,
              items: [...previous.pickupPoints.items, ...fetchMoreResult.pickupPoints.items],
            },
          }
          : previous
      ),
    });
  }, [loadedPage, totalPages]);

  const storeOptions = [
    { label: 'Selecciona una tienda...', value: '' },
    ...(pickupPointsData?.pickupPoints?.items ?? [])
      .filter((point) => point.active)
      .map((point) => ({ label: point.name, value: point.name })),
  ];

  const [markReceivedAtStore, { loading }] = useMutation(MARK_RECEIVED_AT_STORE, {
    onCompleted: (data) => {
      const { customerTicket, errors } = data.customerTicketMarkReceivedAtStore;
      if (customerTicket) {
        onToast('Producto marcado como recibido en tienda');
        modalRef.current?.close();
        onRefetch();
      } else {
        setFormError(errors?.[0]?.message || I18n.t('messages.one_error'));
      }
    },
    onError: () => setFormError(I18n.t('messages.request_error')),
  });

  const openModal = () => {
    setPickupPointName('');
    setFormError(null);
    modalRef.current?.open();
  };

  const handleSubmit = () => {
    setFormError(null);
    markReceivedAtStore({ variables: { id: customerTicketId, address: pickupPointName } });
  };

  return (
    <>
      <Banner title="Producto pendiente de recibir en tienda" status="warning">
        <p>Marca el producto como recibido para iniciar su traslado a CEDI.</p>
        <div className="mt12">
          <Button onClick={openModal}>Marcar recibido en tienda</Button>
        </div>
      </Banner>

      <GeneralModal
        ref={modalRef}
        title="Marcar recibido en tienda"
        primaryAction={{
          content: 'Confirmar',
          onAction: handleSubmit,
          loading,
          disabled: !pickupPointName,
        }}
        secondaryActions={[{ content: 'Cancelar', onAction: () => modalRef.current?.close() }]}
      >
        {formError && (
          <div className="mb8">
            <Banner status="critical">{formError}</Banner>
          </div>
        )}
        <Form onSubmit={(e) => { e.preventDefault(); handleSubmit(); }}>
          <Select
            label="Tienda"
            options={storeOptions}
            value={pickupPointName}
            onChange={setPickupPointName}
          />
        </Form>
      </GeneralModal>
    </>
  );
};

// Same outcome as accepting the store -> CEDI transfer, for a ticket with no pending lane task.
// asButton renders only the button, for a banner that already explains the situation
const ReceiveWithoutTaskAction = ({ customerTicketId, onToast, onRefetch, asButton = false }) => {
  const modalRef = useRef(null);
  const [formError, setFormError] = useState(null);

  const [receiveWithoutTask, { loading }] = useMutation(RECEIVE_WITHOUT_TASK, {
    onCompleted: (data) => {
      const { customerTicket, errors } = data.customerTicketReceiveWithoutTask;
      if (customerTicket) {
        onToast('Producto recibido en CEDI');
        modalRef.current?.close();
        onRefetch();
      } else {
        setFormError(errors?.[0]?.message || I18n.t('messages.one_error'));
      }
    },
    onError: () => setFormError(I18n.t('messages.request_error')),
  });

  const openModal = () => {
    setFormError(null);
    modalRef.current?.open();
  };

  return (
    <>
      { asButton ?
        <Button onClick={openModal}>Recibir producto</Button>
      :
        <Banner title="Producto pendiente de recibir, sin tarea asociada" status="warning">
          <p>Este RMA no tiene una tarea pendiente que traiga el producto. Si ya llegó al CEDI, regístralo aquí.</p>
          <div className="mt12">
            <Button onClick={openModal}>Recibir producto</Button>
          </div>
        </Banner>
      }

      <GeneralModal
        ref={modalRef}
        title="Recibir producto en CEDI"
        primaryAction={{
          content: 'Confirmar recepción',
          onAction: () => receiveWithoutTask({ variables: { id: customerTicketId } }),
          loading,
          disabled: false,
        }}
        secondaryActions={[{ content: 'Cancelar', onAction: () => modalRef.current?.close() }]}
      >
        {formError && (
          <div className="mb8">
            <Banner status="critical">{formError}</Banner>
          </div>
        )}
        <p>El producto queda en CEDI y se habilita Elegir reintegro, igual que al aceptar el traslado de una tarea.</p>
      </GeneralModal>
    </>
  );
};

const RECEIVE_WITHOUT_TASK = gql`
mutation($id: ID!){
  customerTicketReceiveWithoutTask(id: $id) {
    customerTicket {
      id
      productStatus
    }
    errors {
      key
      message
    }
  }
}
`;

const MARK_RECEIVED_PICKUP_POINTS_QUERY = gql`
  query MarkReceivedAtStorePickupPoints($page: Int!) {
    pickupPoints(pagination: { page: $page, items: 100 }) {
      items {
        id
        name
        active
      }
      metadata {
        page
        pages
      }
    }
  }
`;

const TicketCancelButton = ({ customerTicket, onToast, onRefetch }) => {
  const [isLoading, setIsLoading] = useState(false);
  const cancellationRef = useRef(null);
  
  return (
    <>
      <PageActions
        secondaryActions={[
          {
            content: "Cancelar solicitud",
            destructive: true,
            loading: isLoading,
            onAction: () => cancellationRef.current?.handleCancelAction()
          }
        ]}
      />
      <CustomerTicketCancellation 
        ref={cancellationRef}
        customerTicket={customerTicket}
        onToast={onToast}
        onRefetch={onRefetch}
        onLoadingChange={setIsLoading}
      />
    </>
  );
};

const CustomerTicketCancellation = React.forwardRef(({ customerTicket, onToast, onRefetch, onLoadingChange }, ref) => {
  const modalRef = useRef(null);
  
  const [formState, setFormState] = useState({
    reason: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [cancelTicket, { loading: loadingCancel }] = useMutation(CANCEL_TICKET, {
    onCompleted: (dataCancel) => {
      const customerTicketUpdated = dataCancel.customerTicketCancel.customerTicket;
      if(customerTicketUpdated?.id){
        onToast(I18n.t("messages.update_successful"));
        modalRef.current?.close();
        onRefetch();
      }
      else{
        console.log("Errors", dataCancel);
        onToast(I18n.t("messages.one_error"));
      }
      setIsSubmitting(false);
      onLoadingChange(false);
    },
    onError: (error) => {
      console.log("Error", error);
      onToast(I18n.t("messages.request_error"));
      setIsSubmitting(false);
      onLoadingChange(false);
    }
  });

  useEffect(() => {
    onLoadingChange(isSubmitting || loadingCancel);
  }, [isSubmitting, loadingCancel, onLoadingChange]);

  const handleCancelAction = () => {
    modalRef.current?.open();
  };

  React.useImperativeHandle(ref, () => ({
    handleCancelAction
  }));

  const handleSubmit = () => {
    setIsSubmitting(true);
    onLoadingChange(true);
    cancelTicket({
      variables: {
        id: customerTicket.id,
        reason: formState.reason
      }
    });
  };

  return (
    <>
      <GeneralModal
        ref={modalRef}
        title="Cancelar solicitud"
        primaryAction={{
          content: "Cancelar",
          onAction: handleSubmit,
          destructive: true,
          loading: isSubmitting || loadingCancel,
        }}
        secondaryActions={[
          { content: "Volver", onAction: () => modalRef.current?.close() },
        ]}
      >
        <Form onSubmit={(e) => { e.preventDefault(); handleSubmit(); }}>
          <div className='polaris-input-multiline-container mb8'>
            <TextField
              label="Razón de cancelación"
              value={formState.reason}
              onChange={(value) => setFormState({...formState, reason: value})}
              multiline={4}
              placeholder="Indique el motivo de la cancelación..."
            />
          </div>
        </Form>
      </GeneralModal>
    </>
  );
});

const CustomerTicketDetailsWithApollo = (props) => {
  return (
    <ApolloProvider client={adminClient}>
      <AppProvider>
        <Frame>
          <CustomerTicketDetails {...props} />
        </Frame>
      </AppProvider>
    </ApolloProvider>
  )
};

const RECORD_GET = gql`
  fragment TeamData on Team {
    id
    name
  }

  fragment CourierData on Courier{
    id
    name
    team{
      ...TeamData
    }
  }

  query($id: ID!){
    customerTicket(id: $id) {
      id
      name
      customerKind
      providerName
      providerCustomerId
      customerEmail
      customerName
      keyCustomerName
      customerPhone
      providerLineItemId
      moneyProductOriginalTotal
      productTitle
      productSku
      productImageUrl
      ticketKind
      reason
      description
      resolutionPreference
      retrievalMethod
      receivableAtStore
      receivableWithoutTask
      retrievalNotes
      retrievalDate
      address
      lat
      lon
      province
      canton
      district
      zip
      awaitingPickupLocation
      brightpearlSalesCreditId

      status
      requireItems
      approvalKind
      itemsReceived
      productStatus
      resolutionAction
      laneDisplayId
      pendingAction
      slaConsumedPercent
      slaState
      createdAt
      rejectReason

      sendableToSupplier
      conditionedOnWorkshop
      assignedTo {
        id
        name
      }
      supplierCase {
        id
        displayId
        pendingAction
        slaConsumedPercent
        slaState
        status
        supplierResolution
        blocksCustomerResolution
        supplierCreditNoteReference
        workshopCaseNumber
        creditNoteIssuedOn
        workshopCaseNumberEditable
        readOnly
        notes
        sentAt
        slaDueAt
        workshop {
          id
          name
          contact
        }
        supplier {
          id
          name
        }
        assignedTo {
          id
          name
        }
        resolvable
        creditNotePending
        returnTaskCreatable
        workshopDeliveryRegistrable
        returnReceivableWithoutTask
        tasks {
                  id
          name
          status
          rmaLeg
          movement
          movementLabel
          routeOrigin
          routeDestination
          adminUrl
          archived
          team { id name }
          courier {
            id
            name
            team { id name }
          }
        }
        resourceLogs {
          id
          displayMessage
          eventDatetime
          notes
        }
      } # supplierCase

      relatedOrderCustomerTickets {
        id
        name
        ticketKind
        displayId
      }

      media{
        url
        identifier
        contentType
      }

      resourceLogs{
        id
        displayMessage
        eventDatetime
        notes
      }

      relatedDeliveryRecordType
      relatedDeliveryRecordId

      pickupPoint{
        id
        name
        address
        schedule
      }

      displayProviderOrderId
      providerOrderUrl
      brightpearlSalesCreditUrl
      approvable
      rejectable
      escalable
      resolutionActionable
      readOnly
      requireItemsDisplayable

      task{
        id
        name
        status
        createdAt
        adminUrl
        courier{
          ...CourierData
        }
      } # task

      customerLaneTasks {
                id
          name
          status
          rmaLeg
          movement
          movementLabel
          routeOrigin
          routeDestination
          adminUrl
          archived
          team { id name }
          courier {
            id
            name
            team { id name }
          }
      }

      relatedDeliveryRecord{
        ... on Order{
          id
          orderNumber
          adminUrl

          courier{
            ...CourierData
          }
        } # on Order

        ... on DispatchOrder{
          id
          name
          orderNumber

          dispatchControl{
            id
            name
            team{
              ...TeamData
            }
          }
        } # on DispatchOrder
      } # relatedDeliveryRecord
      
    } # customerTicket
  }
`;

const CANCEL_TICKET = gql`
mutation($id: ID!, $reason: String!){
  customerTicketCancel(id: $id, reason: $reason) {
    customerTicket {
      id
    }
    errors {
      key
      message
    }
  }
}
`;

const REGENERATE_PICKUP_LOCATION_LINK = gql`
mutation($id: ID!){
  customerTicketRegeneratePickupLocationLink(id: $id) {
    payload
    errors {
      key
      message
    }
  }
}
`;

const MARK_RECEIVED_AT_STORE = gql`
mutation($id: ID!, $address: String!){
  customerTicketMarkReceivedAtStore(id: $id, address: $address) {
    customerTicket {
      id
      productStatus
    }
    errors {
      key
      message
    }
  }
}
`;

const statusToBannerStatus = {
  "NEW_TICKET": "",
  "REJECTED": "critical",
  "APPROVED": "info",
  "APPROVED_AWAITING_ITEMS": "info",
  "AWAITING_TECHNICAL_VISIT": "info",
  "WAITING_RESOLUTION": "info",
  "PROCESSING_RESOLUTION": "info",
  "ESCALATED": "warning",
  "APPROVED_AND_COMPLETED": "success",
  "ESCALATED": "warning",
}

CustomerTicketDetails.propTypes = propTypes;
CustomerTicketDetails.defaultProps = defaultProps;

export default CustomerTicketDetailsWithApollo;